import React, { useRef } from 'react';
import PropTypes from 'prop-types';
import { i18next } from '@yola/ws-sdk';
import { Modal, PanelGroup, Panel, designSystem } from '@yola/ws-ui';
import bowser from 'yola-bowser';
import constants from '../constants';
import verifiers from '../verifiers';
import GeneralTab from './general-tab';
import AdvancedTab from './advanced-tab';

const {
  DialogHeader,
  DialogHeaderTitle,
  Tabs,
  TabsList,
  Tab,
  TabsContentList,
  TabContent,
  DialogButton,
  DialogButtonGroup,
  DialogButtonGroupSeparator,
  FloatingAction,
} = designSystem;

const {
  common: {
    MODAL_WIDTH,
    PANEL_HEIGHT,
    MODAL_HEIGHT,
    MODAL_HEIGHT_WITH_TABS,
    MIN_FORM_SETTINGS_MODAL_HEIGHT,
  },
  tabIdentifiers,
} = constants;

const ContactFormSettingsDialog = ({
  control,
  fields,
  modalPosition,
  activeTabId,
  isGdprEnabled,
  onTabChange,
  onAddField,
  onEditField,
  onChangeFieldOrder,
  onDeleteField,
  onGdprChange,
  onSubmit,
  onCancel,
  onModalOverlayClick,
  onModalPositionUpdate,
}) => {
  const modalContentRef = useRef(null);

  const isMobile = bowser.mobile;
  const isBuilderEnabled = verifiers.isBuilderEnabled();
  const modalHeight = isBuilderEnabled ? MODAL_HEIGHT_WITH_TABS : MODAL_HEIGHT;

  return (
    <Modal
      modalContentRef={modalContentRef}
      offsetX={modalPosition.offsetX}
      offsetY={modalPosition.offsetY}
      width={MODAL_WIDTH}
      maxWidth={MODAL_WIDTH}
      minWidth={MODAL_WIDTH}
      height={modalHeight}
      minHeight={MIN_FORM_SETTINGS_MODAL_HEIGHT}
      fullscreen={isMobile}
      dragHandleSelector=".ws-drag-trigger"
      className="ws-contact-form-settings-modal"
      onOverlayClick={onModalOverlayClick}
      onDragEnd={onModalPositionUpdate}
    >
      <PanelGroup height="100%">
        <Panel
          height={PANEL_HEIGHT}
          align="middle"
          className="ws-drag-trigger"
          theme="gray-100"
          corners="squared"
          style={{ borderBottom: 'none' }}
        >
          <DialogHeader>
            <DialogHeaderTitle>{i18next.t('Form settings')}</DialogHeaderTitle>
          </DialogHeader>
        </Panel>
        <div className="ws-contact-form-settings-modal__tabs">
          <Tabs activeTabId={activeTabId} onTabChange={onTabChange}>
            <TabsList variant="fullWidth">
              <Tab tabId={tabIdentifiers.GENERAL} label={i18next.t('General')} />
              <Tab tabId={tabIdentifiers.ADVANCED} label={i18next.t('Advanced')} />
            </TabsList>
            <TabsContentList>
              <TabContent tabId={tabIdentifiers.GENERAL}>
                <GeneralTab
                  modalContentRef={modalContentRef}
                  fields={fields}
                  onEditField={onEditField}
                  onChangeFieldOrder={onChangeFieldOrder}
                  onDeleteField={onDeleteField}
                />
              </TabContent>
              <TabContent tabId={tabIdentifiers.ADVANCED}>
                <AdvancedTab
                  control={control}
                  isGdprEnabled={isGdprEnabled}
                  onGdprChange={onGdprChange}
                />
              </TabContent>
            </TabsContentList>
          </Tabs>
        </div>
        <Panel height={PANEL_HEIGHT} align="middle" corners="squared">
          <DialogButtonGroup>
            <DialogButton disabled={!fields.length} onClick={onSubmit}>
              {i18next.t('Submit')}
            </DialogButton>
            <DialogButtonGroupSeparator />
            <DialogButton onMouseDown={onCancel} onClick={() => {}}>
              {i18next.t('Cancel')}
            </DialogButton>
          </DialogButtonGroup>
        </Panel>
      </PanelGroup>
      {isBuilderEnabled && activeTabId === tabIdentifiers.GENERAL && (
        <div className="ws-contact-form-settings-modal__floating-action-wrapper">
          <FloatingAction onClick={onAddField} />
        </div>
      )}
    </Modal>
  );
};

ContactFormSettingsDialog.propTypes = {
  control: PropTypes.shape({}).isRequired,
  fields: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
  modalPosition: PropTypes.shape({
    offsetX: PropTypes.number,
    offsetY: PropTypes.number,
  }).isRequired,
  activeTabId: PropTypes.string.isRequired,
  isGdprEnabled: PropTypes.bool.isRequired,
  onTabChange: PropTypes.func.isRequired,
  onAddField: PropTypes.func.isRequired,
  onEditField: PropTypes.func.isRequired,
  onChangeFieldOrder: PropTypes.func.isRequired,
  onDeleteField: PropTypes.func.isRequired,
  onGdprChange: PropTypes.func.isRequired,
  onSubmit: PropTypes.func.isRequired,
  onCancel: PropTypes.func.isRequired,
  onModalOverlayClick: PropTypes.func.isRequired,
  onModalPositionUpdate: PropTypes.func.isRequired,
};

export default ContactFormSettingsDialog;
